import { useCallback } from "react";
import { ModalLayout } from "../common/ModalLayout";
import { Sparkles, X } from "lucide-react";

import { useLessonForm } from "./hooks/useLessonForm";
import { generateLesson } from "./api";
import { MESSAGES } from "./constants";
import { pdf } from "@react-pdf/renderer";
import LessonPDF from "./LessonPDF";

import { FormStep, LoadingStep, ErrorStep, ResultStep } from "./steps/__init__";

import type { CreateLessonAIProps } from "./types";

import { createMaterial } from "../../../../lib/api";
import { useAuth } from "../../../../app/AuthContext";
import { useSchoolProfile } from "../../../../app/SchoolProfileContext";

export function CreateLessonAI({
  isOpen,
  onClose,
  subjects = ["Matemática", "Português", "Ciências", "História", "Geografia"],
  classes = ["1º Ano", "2º Ano", "3º Ano", "4º Ano", "5º Ano"],
  onLessonGenerated,
}: CreateLessonAIProps) {
  const { token } = useAuth();
  const { activeProfile } = useSchoolProfile();
  const {
    step,
    formData,
    generatedLesson,
    error,
    loadingMessage,
    handleChange,
    setStep,
    setGeneratedLesson,
    setError,
    setLoadingMessage,
    resetForm,
  } = useLessonForm();

  const handleFormChange = useCallback(
    (field: keyof typeof formData, value: any) => {
      handleChange(field, value);
    },
    [handleChange],
  );

  const handleGenerateLesson = useCallback(async () => {
    try {
      setStep("loading");
      setError("");

      const lesson = await generateLesson(formData, (message) => {
        setLoadingMessage(message);
      });

      setGeneratedLesson(lesson);
      setStep("result");
      onLessonGenerated?.(lesson);

      // Automatic saving in FilesManager
      if (token && activeProfile) {
        const payload = {
          title: lesson.title,
          content: JSON.stringify({
            kind: "ai_lesson_plan",
            data: lesson,
            topic: lesson.topic,
            subject: lesson.subject,
            grade: lesson.grade,
          }),
          type: "document",
          school_profile_id: activeProfile.id,
        };
        createMaterial(payload, token)
          .then(() => {
            console.log(
              "CreateLessonAI: Material criado com sucesso, disparando evento...",
            );
            window.dispatchEvent(
              new CustomEvent("materialCreated", {
                detail: { type: "lesson_plan" },
              }),
            );
          })
          .catch(console.error);
      }
    } catch (err) {
      const errorMessage =
        err instanceof Error ? err.message : MESSAGES.ERROR.GENERATION_FAILED;
      setError(errorMessage);
      setStep("error");
    }
  }, [
    formData,
    setStep,
    setError,
    setLoadingMessage,
    setGeneratedLesson,
    onLessonGenerated,
  ]);

  const handleBackToForm = useCallback(() => {
    setStep("form");
    setError("");
  }, [setStep, setError]);

  const handleRetry = useCallback(() => {
    handleBackToForm();
  }, [handleBackToForm]);

  const handleSaveLesson = useCallback(() => {
    if (generatedLesson) {
      onLessonGenerated?.(generatedLesson);
      onClose();
    }
  }, [generatedLesson, onLessonGenerated, onClose]);

  const handleCopyLesson = useCallback(() => {
    if (generatedLesson) {
      const content = `
${generatedLesson.title}

Tópico: ${generatedLesson.topic}
Duração: ${generatedLesson.duration} minutos
Dificuldade: ${generatedLesson.difficulty}

OBJETIVOS:
${generatedLesson.objectives.map((obj) => `- ${obj}`).join("\n")}

${Object.values(generatedLesson.lessonPlan || {})
  .map(
    (section: any) => `
    ${section.title || "Seção"}
    ${section.description || ""}
    Atividades: ${Array.isArray(section.activities) ? section.activities.join(", ") : section.activities || "N/A"}
  `,
  )
  .join("\n")}
      `.trim();

      navigator.clipboard.writeText(content).then(() => {
        alert(MESSAGES.SUCCESS.COPIED);
      });
    }
  }, [generatedLesson]);

  const handleDownloadLesson = useCallback(async () => {
    if (generatedLesson) {
      try {
        const doc = <LessonPDF lesson={generatedLesson} />;
        const blob = await pdf(doc).toBlob();

        const url = URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.download = `${generatedLesson.title}.pdf`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
      } catch (error) {
        console.error("Erro ao gerar PDF:", error);
        alert("Erro ao gerar o PDF. Tente novamente.");
      }
    }
  }, [generatedLesson]);

  const handleRegenerateLesson = useCallback(() => {
    handleGenerateLesson();
  }, [handleGenerateLesson]);

  if (!isOpen) return null;

  return (
    <ModalLayout
      isOpen={isOpen}
      onClose={onClose}
      title="Gerar Plano de Aula com IA"
      subtitle="Crie planos de aula personalizados em segundos"
      icon={<Sparkles className="w-7 h-7 text-(--primary-color)" />}
    >
      <div className="p-4 sm:p-6">
        <div className="mb-6">
          {step === "form" && (
            <FormStep
              formData={formData}
              subjects={subjects}
              classes={classes}
              onFormChange={handleFormChange}
              onGenerate={handleGenerateLesson}
            />
          )}

          {step === "loading" && <LoadingStep message={loadingMessage} />}

          {step === "error" && (
            <ErrorStep
              error={error}
              onRetry={handleRetry}
              onBack={handleBackToForm}
            />
          )}

          {step === "result" && generatedLesson && (
            <ResultStep
              generatedLesson={generatedLesson}
              onSave={handleSaveLesson}
              onCopy={handleCopyLesson}
              onDownload={handleDownloadLesson}
              onRegenerate={handleRegenerateLesson}
            />
          )}
        </div>
      </div>
    </ModalLayout>
  );
}

export default CreateLessonAI;
